/**
 * Conversation List Service — BUB-135
 * Browse all Paul ↔ customer conversations (live pull from n8n via OS backend).
 */

import api from "../../../shared/api.jsx";

/**
 * @param {Object} [filters]
 * @param {string} [filters.search]
 * @param {string} [filters.since]
 * @param {'open'|'closed'|'archived'|'all'} [filters.status]
 * @param {string} [filters.source]
 * @param {boolean} [filters.needsAttention]
 * @param {number} [filters.page]
 * @param {number} [filters.pageSize]
 * @returns {Promise<Object>}
 */
export const getConversationList = async (filters = {}) => {
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.since) params.since = filters.since;
  if (filters.status) params.status = filters.status;
  if (filters.source) params.source = filters.source;
  if (filters.needsAttention) params.needsAttention = "true";
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;

  const response = await api.get("/leads/conversations", { params });
  return response.data ?? response;
};

/**
 * Fetch the full conversation for a customer phone (channel id) that is not yet
 * linked to an OS lead. Used by the conversation drill-down drawer.
 * @param {string} phone
 * @param {{ mode?: 'executive'|'structured', summaryStyle?: 'executive'|'structured', leadId?: string }} [options]
 * @returns {Promise<Object>}
 */
export const getConversationByPhone = async (phone, options = {}) => {
  const params = { channelId: phone };
  const mode = options.mode || options.summaryStyle;
  if (mode === "structured") {
    params.mode = "structured";
  }
  if (options.leadId) {
    params.leadId = options.leadId;
  }
  const response = await api.get("/leads/conversations/detail", { params });
  return response.data ?? response;
};

/**
 * Close a conversation (n8n sets inbox_status = closed).
 * @param {{ channelId: string, leadId?: string, reason?: string }} params
 * @returns {Promise<Object>}
 */
export const closeConversation = async ({ channelId, leadId, reason }) => {
  const response = await api.post("/leads/conversations/close", {
    channelId,
    leadId,
    reason,
  });
  return response.data ?? response;
};

/**
 * Start desktop WhatsApp handoff (optional assign self).
 * @param {{ channelId: string, leadId?: string, assignSelf?: boolean }} params
 * @returns {Promise<Object>}
 */
export const handoffConversation = async ({
  channelId,
  leadId,
  assignSelf = false,
}) => {
  const response = await api.post("/leads/conversations/handoff", {
    channelId,
    leadId,
    assignSelf,
  });
  return response.data ?? response;
};

/**
 * Assign or reassign the current op to a conversation.
 * @param {{ channelId: string, leadId?: string, assignedTo?: string }} params
 * @returns {Promise<Object>}
 */
export const assignConversation = async ({ channelId, leadId, assignedTo }) => {
  const response = await api.post("/leads/conversations/assign", {
    channelId,
    leadId,
    assignedTo,
  });
  return response.data ?? response;
};

/**
 * Release conversation assignment.
 * @param {{ channelId: string, leadId?: string }} params
 * @returns {Promise<Object>}
 */
export const releaseConversation = async ({ channelId, leadId }) => {
  const response = await api.post("/leads/conversations/release", {
    channelId,
    leadId,
  });
  return response.data ?? response;
};

/**
 * List the current user's pinned conversations.
 * @returns {Promise<{ pins: Array<{ channelId: string, pinnedAt: string, order: number }> }>}
 */
export const getPinnedConversations = async () => {
  const response = await api.get("/leads/conversations/pins");
  return response.data ?? response;
};

/**
 * Pin a conversation by channel id (phone).
 * @param {string} channelId
 * @returns {Promise<Object>}
 */
export const pinConversation = async (channelId) => {
  const response = await api.post("/leads/conversations/pins", { channelId });
  return response.data ?? response;
};

/**
 * Unpin a conversation by channel id (phone).
 * @param {string} channelId
 * @returns {Promise<Object>}
 */
export const unpinConversation = async (channelId) => {
  const response = await api.delete("/leads/conversations/pins", {
    body: JSON.stringify({ channelId }),
    headers: { "Content-Type": "application/json" },
  });
  return response.data ?? response;
};
